import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Button,
  Alert,
  Divider,
  Stack,
} from '@mui/material';
import { ChevronDown as ExpandMoreIcon } from 'lucide-react';

const PRIORITY_COLORS = { high: 'error', medium: 'warning', low: 'default' };

const ConfidenceBadge = ({ value }) => {
  if (value == null) return null;
  const pct = Math.round(value * 100);
  const color = pct >= 75 ? 'success' : pct >= 50 ? 'warning' : 'error';
  return <Chip label={`${pct}% confidence`} color={color} size="small" />;
};

const ResultsStep = ({ result, analysis }) => {
  const navigate = useNavigate();

  if (!result) {
    return <Alert severity="info">No results available yet.</Alert>;
  }

  const {
    is_denial_letter,
    denial_type,
    payer_name,
    patient_name,
    claim_number,
    policy_number,
    date_of_service,
    date_of_denial,
    appeal_deadline,
    denial_codes_raw = [],
    denial_category,
    plain_english_explanation,
    appeal_requirements,
    payer_specific_notes,
    next_steps = [],
    overall_confidence,
    confidence_flags = [],
    source_highlights = [],
  } = result;

  const handleGenerateAppeal = () => {
    // Navigate to MinimalAppeal flow with denial metadata pre-filled
    navigate('/appeals/new', {
      state: {
        fromDenialAnalysis: true,
        denialAnalysisId: analysis?.id,
        prefill: {
          payer_name,
          patient_name,
          claim_number,
          denial_code: denial_codes_raw.map(c => c.code).join(', '),
          denial_text: plain_english_explanation,
        },
      },
    });
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h6">Analysis Results</Typography>
        <ConfidenceBadge value={overall_confidence} />
      </Box>

      {!is_denial_letter && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          This document may not be a denial letter. Results may be limited.
        </Alert>
      )}

      {confidence_flags.length > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <strong>Confidence Flags:</strong>{' '}
          {confidence_flags.map(f => `${f.field}: ${f.note}`).join('; ')}
        </Alert>
      )}

      {/* Classification */}
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon size={16} />}>
          <Typography fontWeight={600}>Classification</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing={1}>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Chip label={`Type: ${denial_type || 'Unknown'}`} variant="outlined" />
              <Chip label={`Category: ${denial_category || 'Unknown'}`} variant="outlined" />
            </Box>
          </Stack>
        </AccordionDetails>
      </Accordion>

      {/* Metadata */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon size={16} />}>
          <Typography fontWeight={600}>Claim Details</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Table size="small">
            <TableBody>
              {[
                ['Payer', payer_name],
                ['Patient', patient_name],
                ['Claim Number', claim_number],
                ['Policy Number', policy_number],
                ['Date of Service', date_of_service],
                ['Date of Denial', date_of_denial],
                ['Appeal Deadline', appeal_deadline],
              ].filter(([, v]) => v).map(([label, value]) => (
                <TableRow key={label}>
                  <TableCell sx={{ fontWeight: 600, width: '40%' }}>{label}</TableCell>
                  <TableCell>{value}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </AccordionDetails>
      </Accordion>

      {/* Denial Codes */}
      {denial_codes_raw.length > 0 && (
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon size={16} />}>
            <Typography fontWeight={600}>Denial Codes ({denial_codes_raw.length})</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Code</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Description</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {denial_codes_raw.map((code, idx) => (
                  <TableRow key={idx}>
                    <TableCell><strong>{code.code}</strong></TableCell>
                    <TableCell><Chip label={code.type} size="small" /></TableCell>
                    <TableCell>{code.carc_full_text || code.description}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </AccordionDetails>
        </Accordion>
      )}

      {/* Interpretation */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon size={16} />}>
          <Typography fontWeight={600}>Plain-English Explanation</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography sx={{ mb: 2 }}>{plain_english_explanation}</Typography>
          {appeal_requirements && (
            <>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" gutterBottom>Appeal Requirements</Typography>
              <Typography>{appeal_requirements}</Typography>
            </>
          )}
          {payer_specific_notes && (
            <>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" gutterBottom>Payer-Specific Notes</Typography>
              <Typography>{payer_specific_notes}</Typography>
            </>
          )}
        </AccordionDetails>
      </Accordion>

      {/* Next Steps */}
      {next_steps.length > 0 && (
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon size={16} />}>
            <Typography fontWeight={600}>Next Steps ({next_steps.length})</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={2}>
              {next_steps.map((step, idx) => (
                <Paper key={idx} variant="outlined" sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
                    <Chip label={step.priority} color={PRIORITY_COLORS[step.priority] || 'default'} size="small" />
                    {step.deadline_sensitive && <Chip label="Deadline Sensitive" color="error" size="small" variant="outlined" />}
                  </Box>
                  <Typography fontWeight={600}>{step.action}</Typography>
                  {step.rationale && <Typography variant="body2" color="text.secondary">{step.rationale}</Typography>}
                </Paper>
              ))}
            </Stack>
          </AccordionDetails>
        </Accordion>
      )}

      {/* Source Highlights */}
      {source_highlights.length > 0 && (
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon size={16} />}>
            <Typography fontWeight={600}>Source Highlights</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={1}>
              {source_highlights.map((h, idx) => (
                <Box key={idx} sx={{ p: 1, bgcolor: 'grey.100', borderRadius: 1 }}>
                  <Typography variant="caption" color="text.secondary">{h.field}</Typography>
                  <Typography variant="body2" sx={{ fontStyle: 'italic' }}>"{h.snippet}"</Typography>
                </Box>
              ))}
            </Stack>
          </AccordionDetails>
        </Accordion>
      )}

      <Box sx={{ mt: 3 }}>
        <Button variant="contained" size="large" fullWidth onClick={handleGenerateAppeal}>
          Generate Appeal Letter
        </Button>
      </Box>
    </Box>
  );
};

export default ResultsStep;
